/**
 * The pin gesture's one hard part: turn a click on the SCREEN into a point in
 * BIND-WORLD (ADR-022 D1 — "click the model at the fingertip; name what you
 * hit").
 *
 * WHY THIS IS NOT `raycast().point`. A pin is stored in bind-world, because
 * that is the only space it can outlive its analysis in (ADR-022 §5.1) — and
 * the subject on screen is usually PLAYING A CLIP. The ray hits the POSED
 * surface, so `intersection.point` is where the fingertip is *right now*, which
 * on a waving hand is nowhere near where the fingertip rests. Storing that
 * would place a digit into empty space and the graft-time region check would
 * refuse it — after the person had already answered. Worse, on a nearly-still
 * clip it would be off by millimetres and refuse nothing at all: a wrong answer
 * that measures plausible is exactly the failure this codebase keeps finding
 * ([[played-frame-not-bind-frame]] — a magnitude test cannot see it).
 *
 * THE CONVERSION, and why it needs no inverse skinning. A triangle's barycentric
 * coordinates are affine and therefore identical in both frames: whatever the
 * bones did to the triangle, the hit sits at the same (u,v,w) of its vertices.
 * So we read the barycentric of the hit against the POSED triangle, then apply
 * those same weights to the same three vertices' BIND positions. Exact for the
 * interior of any triangle, no matrix inverted, and it degrades to a named
 * refusal rather than an approximation when the triangle is degenerate.
 *
 * BIND-WORLD MEANS `position × bindMatrix` — the same definition
 * `isolateHandRegion` and `bindPoseVertices` use, and therefore the same space
 * as `TipCluster.tip` and everything the voxel grid was built from. There is
 * exactly one such definition in this tree and this file does not add a second:
 * it is stated here because a click is where a second one would be tempting.
 *
 * View-adjacent but decision-free: it reports where the click landed and how
 * far that is from the hand being pinned. Whether that is close enough is the
 * panel's rule, not this module's.
 * Depends on: three (raycast), adapter-three (sole skinned mesh).
 * Depended on by: main (wires it to the Hands panel), hands-panel (type only).
 */
import { Matrix4, Raycaster, Triangle, Vector2, Vector3, type Camera, type Object3D } from 'three';
import { soleSkinnedMesh } from '@harryhausen/adapter-three';
import type { Vec3 } from './hand-evidence.js';

/** A click that CANNOT be turned into a bind-world point — a wiring or mesh fault, named. */
export class PinGestureRefusal extends Error {}

export interface PinHit {
  /** Where the click landed, in BIND-WORLD — the space a pin is stored in. */
  readonly bindWorld: Vec3;
  /** Where it landed on the POSED model, world space — for the dot's live position. */
  readonly posedWorld: Vec3;
  /**
   * The three MESH vertex indices of the triangle that was hit. This is what
   * lets a caller ask "is this on the hand I am pinning?" EXACTLY — the hand
   * region is a set of vertex indices, so membership is a lookup with no
   * threshold to tune. The distance-to-nearest-vertex test this replaced
   * refused a click dead on the goblin's fingertip, because on a low-poly hand
   * the nearest vertex to a surface point is millimetres away (measured
   * 2026-08-12, through the e2e).
   */
  readonly face: readonly [number, number, number];
  /**
   * The TRIANGLE's index — `face[i]` are its corners, this is its identity in
   * the primitive (ADR-031 T0). A Zone is a set of faces, so the wand's seed
   * and every "is this face in the zone" question are lookups on this number;
   * the vertex triple above answers the hand's question ("is this on the hand
   * I am pinning"), and neither can be derived from the other without the
   * index buffer in hand.
   */
  readonly faceIndex: number;
}

/**
 * The bind-world point under a normalized-device-coordinate click, or null when
 * the ray missed the subject entirely (a click on empty space is not an error;
 * it is a person aiming and missing, and it must not throw).
 */
export function pinHitAt(scene: Object3D, camera: Camera, ndc: { readonly x: number; readonly y: number }): PinHit | null {
  const mesh = soleSkinnedMesh(scene);
  const raycaster = new Raycaster();
  raycaster.setFromCamera(new Vector2(ndc.x, ndc.y), camera);

  // ★ REFRESH THE SKINNED BOUND, OR THE CLICK QUIETLY DOES NOTHING.
  //   `SkinnedMesh.raycast` early-outs against `this.boundingSphere` — the
  //   SKINNED bound, which is right — but three computes it LAZILY ONCE and
  //   then caches it forever ("if the skinned mesh is animated, the bounding
  //   sphere should be recomputed per frame", three's own docs). In the app it
  //   is first computed by the renderer's frustum cull, at whatever pose that
  //   frame happened to be wearing, and never again. A clip that then carries
  //   the hand clear of that stale sphere makes every click a NO-OP: no
  //   refusal, no dot, nothing to notice — the silent failure this codebase
  //   keeps writing rules against.
  //   MEASURED, not assumed (pin-gesture.test.ts): with the bound cached at
  //   bind and the subject posed 40 units out, the cast returns NOTHING; with
  //   this line, it hits and the barycentric conversion lands within 1e-5.
  mesh.computeBoundingSphere();
  const hit = raycaster.intersectObject(mesh, false)[0];
  if (hit === undefined) return null;
  if (hit.face === null || hit.face === undefined) {
    // A hit with no face is a points/lines drawable or a geometry without
    // triangles — there is no triangle to read a barycentric from, so there is
    // no honest conversion. Loud, because it means the subject is not what the
    // panel thinks it is.
    throw new PinGestureRefusal(
      'pin gesture: the ray hit geometry with no triangle face — a bind-world pin cannot be read from it.',
    );
  }

  const { a, b, c } = hit.face;
  if (hit.faceIndex === undefined || hit.faceIndex === null) {
    // three sets faceIndex on every Mesh intersection that carries a face;
    // its absence means a drawable this gesture does not understand.
    throw new PinGestureRefusal('pin gesture: the ray hit a face with no face index — the zone of this click cannot be named.');
  }
  // The POSED triangle, in world. `getVertexPosition` is skin-aware on a
  // SkinnedMesh (it applies the same bone transform the GPU does), which is
  // what makes this the triangle the person actually clicked.
  const pa = mesh.getVertexPosition(a, new Vector3()).applyMatrix4(mesh.matrixWorld);
  const pb = mesh.getVertexPosition(b, new Vector3()).applyMatrix4(mesh.matrixWorld);
  const pc = mesh.getVertexPosition(c, new Vector3()).applyMatrix4(mesh.matrixWorld);
  const bary = Triangle.getBarycoord(hit.point, pa, pb, pc, new Vector3());
  if (bary === null) {
    throw new PinGestureRefusal(
      'pin gesture: the clicked triangle is degenerate (zero area) — no barycentric coordinate exists, ' +
        'so the click cannot be carried back to bind pose. The mesh has collapsed triangles here.',
    );
  }

  // The same three vertices at BIND — `position × bindMatrix`, the definition
  // isolateHandRegion measures in and the voxel grid is embedded in.
  const bind = new Matrix4().copy(mesh.bindMatrix);
  const position = mesh.geometry.getAttribute('position');
  const ba = new Vector3().fromBufferAttribute(position, a).applyMatrix4(bind);
  const bb = new Vector3().fromBufferAttribute(position, b).applyMatrix4(bind);
  const bc = new Vector3().fromBufferAttribute(position, c).applyMatrix4(bind);
  const bindWorld = new Vector3()
    .addScaledVector(ba, bary.x)
    .addScaledVector(bb, bary.y)
    .addScaledVector(bc, bary.z);

  return {
    bindWorld: { x: bindWorld.x, y: bindWorld.y, z: bindWorld.z },
    posedWorld: { x: hit.point.x, y: hit.point.y, z: hit.point.z },
    face: [a, b, c],
    faceIndex: hit.faceIndex,
  };
}

/**
 * Is the clicked triangle part of THIS hand's measured region?
 *
 * The question a panel actually has: a person can and will click the other
 * hand, or the hip, with the right-hand card open, and a pin there would place
 * a finger where nobody meant (and be refused at graft time, one gesture too
 * late). Membership is exact — the region IS a set of mesh vertex indices — so
 * unlike a distance threshold there is nothing here to tune, and nothing that
 * behaves differently on a dense mesh than on a coarse one.
 *
 * ANY of the three vertices is enough: a triangle straddling the wrist cut is
 * still the hand, and the graft's own voxel-distance check (`run-document`)
 * remains the deeper guard against a coordinate that is nowhere near the volume.
 */
export function faceInRegion(
  face: readonly [number, number, number],
  region: { readonly vertexIndices: Uint32Array },
): boolean {
  for (const index of region.vertexIndices) {
    if (index === face[0] || index === face[1] || index === face[2]) return true;
  }
  return false;
}
